/**
 * Property-detail "Open in CRM" control for the app header.
 *
 * Sits immediately left of global search and uses the same `size="sm"`
 * outline button as property Export actions, not the search field height.
 * Same SSI gate and Dynamics URL as the overview button it replaced
 * (DEF-322): staff roles only, hidden in Report Review, and only when the
 * building has a CRM record id the env base can open. Visible on Overview
 * (`tab` absent or `overview`) and Vault Admin (`vault-admin`) only.
 */

import { useQuery } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { buildingsApi } from '@/api/endpoints/buildings';
import { Button } from '@/components/ui/button';
import { buildOpenCrmPropertyUrl } from '@/lib/crm';
import { useIsSSI } from '@/stores/auth.store';
import { isReportReviewSearchParams } from '../lib/report-review-session';

/** `/properties/:id` only — not edit, new, drawings, or the report window. */
const PROPERTY_DETAIL_PATH = /^\/properties\/(\d+)$/;

/**
 * Property detail tabs that keep Open in CRM in the header.
 * Ids match `TABS` in PropertyDetailPage (`overview`, `vault-admin`).
 */
const CRM_HEADER_TAB_IDS = new Set(['overview', 'vault-admin']);

function propertyDetailBuildingId(pathname: string): number | null {
  const match = PROPERTY_DETAIL_PATH.exec(pathname);
  if (!match) return null;
  const id = Number(match[1]);
  return id > 0 ? id : null;
}

/**
 * Active property tab from `?tab=`. A missing tab is Overview, the page
 * default. Legacy `activity` is Report Updates, not Overview.
 */
function propertyDetailTabId(search: string): string {
  const tab = new URLSearchParams(search).get('tab');
  if (tab == null || tab === '') return 'overview';
  if (tab === 'activity') return 'report-updates';
  return tab;
}

export function HeaderOpenInCrm() {
  const { pathname, search } = useLocation();
  const buildingId = propertyDetailBuildingId(pathname);
  const isSsi = useIsSSI();
  const reportReview = isReportReviewSearchParams(new URLSearchParams(search));
  const onCrmTab = CRM_HEADER_TAB_IDS.has(propertyDetailTabId(search));
  const enabled = buildingId != null && isSsi && !reportReview && onCrmTab;

  const { data } = useQuery({
    queryKey: ['building', buildingId ?? 0],
    queryFn: () => buildingsApi.getBuilding(buildingId ?? 0, undefined, { silent: true }),
    enabled,
    retry: false,
  });

  const crmHref = enabled ? buildOpenCrmPropertyUrl(data?.crmUrl) : null;
  if (!crmHref) return null;

  return (
    <Button variant="outline" size="sm" className="shrink-0 gap-1.5" asChild>
      <a href={crmHref} target="_blank" rel="noopener noreferrer">
        <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
        Open in CRM
      </a>
    </Button>
  );
}
